/**
 * @module useTooltip
 *
 * @see https://0.vuetifyjs.com/composables/plugins/use-tooltip
 *
 * @remarks
 * Region-scoped tooltip coordination plugin. Holds shared delay defaults and
 * a small registry of currently-open tooltips so individual tooltip instances
 * can skip their open delay when another tooltip is already visible (the
 * "warmup" pattern from React Aria / Radix).
 *
 * Use the plugin form (`app.use(createTooltipPlugin())`) for app-wide
 * defaults.
 *
 * @example
 * ```ts
 * import { createTooltipPlugin } from '@vuetify/v0'
 *
 * app.use(createTooltipPlugin({ openDelay: 500 }))
 * ```
 */

// Composables
import { createPluginContext } from '#v0/composables/createPlugin'
import { createRegistry } from '#v0/composables/createRegistry'

// Utilities
import { isNull } from '#v0/utilities'
import { onScopeDispose, toRef, toValue } from 'vue'

// Types
import type { RegistryTicket, RegistryTicketInput } from '#v0/composables/createRegistry'
import type { PopoverAdapter } from '#v0/composables/usePopover'
import type { ID } from '#v0/types'
import type { MaybeRefOrGetter, Ref } from 'vue'

/**
 * Options accepted by the tooltip factory.
 *
 * @example
 * ```ts
 * import { createTooltipPlugin } from '@vuetify/v0'
 *
 * app.use(createTooltipPlugin({ openDelay: 500, closeDelay: 100 }))
 * ```
 */
export interface TooltipOptions {
  /** Default open delay in ms. @default 700 */
  openDelay?: MaybeRefOrGetter<number>
  /** Default close delay in ms. @default 150 */
  closeDelay?: MaybeRefOrGetter<number>
  /** Window in ms after a tooltip closes during which the next open is instant. @default 300 */
  skipDelay?: MaybeRefOrGetter<number>
  /** Disable all tooltips in this region. @default false */
  disabled?: MaybeRefOrGetter<boolean>
  /**
   * Positioning adapter for tooltip surfaces in this region. Tooltip-only —
   * Popover / Select / Combobox never see this value. Per-instance `adapter`
   * on `Tooltip.Root` still wins.
   */
  adapter?: PopoverAdapter
}

export interface TooltipContext {
  /**
   * Reactive open delay in ms.
   *
   * @example
   * ```ts
   * const tooltip = useTooltip()
   * console.log(tooltip.openDelay.value)
   * ```
   */
  openDelay: Readonly<Ref<number>>
  /**
   * Reactive close delay in ms.
   *
   * @example
   * ```ts
   * const tooltip = useTooltip()
   * console.log(tooltip.closeDelay.value)
   * ```
   */
  closeDelay: Readonly<Ref<number>>
  /**
   * Reactive skip-window length in ms.
   *
   * @example
   * ```ts
   * const tooltip = useTooltip()
   * console.log(tooltip.skipDelay.value)
   * ```
   */
  skipDelay: Readonly<Ref<number>>
  /**
   * Reactive disabled flag for the region.
   *
   * @example
   * ```ts
   * const tooltip = useTooltip()
   * if (tooltip.disabled.value) return
   * ```
   */
  disabled: Readonly<Ref<boolean>>
  /**
   * True when at least one tooltip is currently open in this region.
   *
   * @example
   * ```ts
   * const tooltip = useTooltip()
   * watch(tooltip.isAnyOpen, open => console.log(open))
   * ```
   */
  isAnyOpen: Readonly<Ref<boolean>>
  /**
   * Returns true when the next opening tooltip should skip its open delay.
   *
   * @remarks
   * The skip rule is two-pronged: skip when another tooltip is already open
   * in the region, or skip when the previous tooltip closed within the
   * `skipDelay` window.
   *
   * @example
   * ```ts
   * const tooltip = useTooltip()
   * const delay = tooltip.shouldSkipOpenDelay() ? 0 : tooltip.openDelay.value
   * ```
   */
  shouldSkipOpenDelay: () => boolean
  /**
   * Register an open tooltip with the region.
   *
   * @example
   * ```ts
   * const tooltip = useTooltip()
   * const ticket = tooltip.register({ id: 'tooltip:1' })
   * ```
   */
  register: (input?: Partial<RegistryTicketInput>) => RegistryTicket
  /**
   * Unregister a tooltip and stamp the close timestamp.
   *
   * @example
   * ```ts
   * const tooltip = useTooltip()
   * tooltip.unregister(ticket.id)
   * ```
   */
  unregister: (id: ID) => void
  /**
   * Region positioning adapter, or `undefined` to fall through to the
   * popover plugin / `V0PopoverAdapter`.
   *
   * @example
   * ```ts
   * const tooltip = useTooltip()
   * tooltip.adapter
   * ```
   */
  adapter: PopoverAdapter | undefined
}

export interface TooltipContextOptions extends TooltipOptions {
  namespace?: string
}

export interface TooltipPluginOptions extends TooltipContextOptions {}

function createTooltip (options: TooltipOptions = {}): TooltipContext {
  const openDelay = toRef(() => toValue(options.openDelay) ?? 700)
  const closeDelay = toRef(() => toValue(options.closeDelay) ?? 150)
  const skipDelay = toRef(() => toValue(options.skipDelay) ?? 300)
  const disabled = toRef(() => toValue(options.disabled) ?? false)

  const registry = createRegistry({ reactive: true, events: true })
  let lastClosedAt: number | null = null

  registry.on('unregister:ticket', () => {
    lastClosedAt = Date.now()
  })

  const isAnyOpen = toRef(() => registry.size > 0)

  function shouldSkipOpenDelay (): boolean {
    if (isAnyOpen.value) return true
    if (isNull(lastClosedAt)) return false
    const elapsed = Date.now() - lastClosedAt
    return elapsed < skipDelay.value
  }

  onScopeDispose(() => registry.dispose(), true)

  return {
    openDelay,
    closeDelay,
    skipDelay,
    disabled,
    isAnyOpen,
    shouldSkipOpenDelay,
    register: registry.register,
    unregister: registry.unregister,
    adapter: options.adapter,
  }
}

/**
 * Synthesized fallback used when `useTooltip()` is called without
 * `app.use(createTooltipPlugin())`. Returns a fresh context with the
 * documented defaults — `<Tooltip.Root>` keeps working but warmup
 * coordination is per-instance instead of region-wide.
 *
 * @example
 * ```ts
 * import { createTooltipFallback } from '@vuetify/v0'
 *
 * const tooltip = createTooltipFallback()
 * console.log(tooltip.openDelay.value) // 700
 * ```
 */
export function createTooltipFallback (): TooltipContext {
  return createTooltip()
}

export const [createTooltipContext, createTooltipPlugin, useTooltip] =
  /* @__PURE__ */ createPluginContext<TooltipPluginOptions, TooltipContext>(
    'v0:tooltip',
    createTooltip,
    {
      fallback: () => createTooltipFallback(),
    },
  )
